<template>
  <BaseModal
    :class="bemBlock"
    :is-open="true"
    :modal-modifier="'pdp-customization-modal'"
    :show-backdrop="true"
    @closed="$emit('customize-modal-closed')"
  >
    <div :class="bemElement('content')">
      <div :class="bemElement('image')">
        <BaseImage
          loading="eager"
          :src="getDefaultImageUrlFromProductImageSet(mainImage)"
          :alt="getDefaultImageAltFromProductImageSet(mainImage)"
          :placeholder="getPlaceholderImageUrlFromProductImageSet(mainImage)"
          :sizes="imageAttributeSizes.IN_STORE_STOCK"
          :srcset="getSrcsetFromProductImageSet(mainImage)"
        />
        <p
          :class="bemElement('customized-text')"
          :style="{ color: customizedColor ? customizedColor : '' }"
        >
          {{ customizedText.toUpperCase() }}
        </p>
      </div>
      <h1 :class="[bemElement('title'), 'the-product-details__title']">
        <span class="title-text">
          {{ $msg('customize.modal.title.1', 'product') }}
        </span>
        <span class="title-text">
          {{ $msg('customize.modal.title.2', 'product') }}
        </span>
      </h1>

      <form
        id="customizationForm"
        ref="form"
        :class="[bemElement('form'), 'form']"
        name="customizationForm"
        novalidate
      >
        <BaseFieldText
          id="customizeFieldText"
          ref="customizeFieldText"
          v-model="customizedText"
          name="customizeFieldText"
          autocomplete="off"
          bem="the-product-details-customization-modal"
          :class="bemElement('input')"
          type="primary"
          input-type="text"
          :label="$msg('customize.modal.input.placeholder', 'product')"
          :pattern="azLettersRegex"
          :maxlength="3"
          :required="true"
          :error-messages="{
            patternMismatch: $msg('error.message.safetext', 'forms'),
            missingError: $msg('error.message.required', 'forms'),
          }"
          @change="onBaseFieldTextChange"
        />

        <div :class="bemElement('colors-wrapper')">
          <p :class="bemElement('subtitle')">
            {{ $msg('customize.modal.color.title', 'product') }}
          </p>

          <BaseColorSwatches
            bem="the-product-details-customization-modal"
            :class="bemElement('swatches')"
            :show-current="true"
            :colors="product.bylCustomizableColors"
            :initial-color="product.bylCustomizableColors[0]"
            :pre-expanded="true"
          />
        </div>

        <!-- Button desktop -->
        <div
          :class="[
            bemElement('add-to-cart'),
            bemElement('add-to-cart--desktop'),
            bemElement('actions'),
            'cart-and-ipay',
            'the-product-details__add-to-cart',
            'the-product-details__actions',
          ]"
        >
          <input type="hidden" class="add-to-cart-url" :value="addToCartUrl" />
          <button
            :class="[
              bemElement('add-to-cart-btn', ['desktop']),
              'the-product-details__add-to-cart-btn',
              'the-product-details__add-to-cart-btn--desktop',
              'add-to-cart',
              'btn',
              'btn-primary',
              'btn-big',
              'interaction--add-to-bag',
            ]"
            type="button"
            :data-pid="product.id"
            data-toggle="modal"
            data-target="#chooseBonusProductModal"
            data-interaction="interaction--add-to-bag"
            :data-interaction-state="
              isValid ? STATE_ADD_TO_BAG.ENABLED : STATE_ADD_TO_BAG.DISABLED
            "
          >
            <BaseSpinner small />
            <span class="default-content">{{
              $msg('button.addtocart', 'product')
            }}</span>
            <span class="swap-content">
              {{ $msg('text.alert.addedtobasket', 'product') }}
            </span>
          </button>
        </div>
      </form>

      <p :class="bemElement('conditions')">
        <span> {{ $msg('customize.modal.conditions.1', 'product') }} </span>
        <span> {{ $msg('customize.modal.conditions.2', 'product') }} </span>
        <span> {{ $msg('customize.modal.conditions.3', 'product') }} </span>
      </p>

      <!-- Button Mobile -->
      <button
        :class="[
          bemElement('add-to-cart-btn', 'mobile'),
          bemElement('add-to-cart--mobile'),
          bemElement('actions'),
          'the-product-details__add-to-cart-btn',
          'the-product-details__add-to-cart-btn--mobile',
          'the-product-details__actions',
          'add-to-cart-mobile',
          'btn',
          'btn--round-black-mobile',
          'interaction--add-to-bag-mobile',
          { disabled: !isValid },
        ]"
        :data-pid="product.id"
        data-size-type="one-size"
        :data-attr-url="addToCartUrlMobile"
        data-toggle="modal"
        data-target="#chooseBonusProductModal"
        data-interaction="interaction--add-to-bag-mobile"
        :data-interaction-state="
          isValid
            ? STATE_ADD_TO_BAG_MOBILE.ENABLED
            : STATE_ADD_TO_BAG_MOBILE.DISABLED
        "
      >
        <BaseSpinner small white />
        <span class="default-content">
          <span class="one-size">
            {{ $msg('button.addtocart', 'product') }}
          </span>
        </span>

        <span class="swap-content">
          {{ $msg('text.alert.addedtobasket.modal', 'product') }}
        </span>
      </button>
    </div>
  </BaseModal>
</template>

<script>
import bemClassesMixin from '../mixins/bemClassesMixin';
import { productBaseMixin } from '../mixins/productPropBaseMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import productPropSizesMixin from '../mixins/productPropSizesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseColorSwatches from './BaseColorSwatches.vue';
import BaseFieldText from './forms/BaseFieldText.vue';
import BaseImage from './BaseImage.vue';
import BaseModal from './BaseModal.vue';
import BaseSpinner from './BaseSpinner.vue';
import clientSideValidation from '../../components/clientSideValidation';

import EventBusConfig from '../config/eventBusConfig';
import getFrontConfig from '../../common/getFrontConfig';
import { getVariationGroupId } from '../../common/helpers/productIdHelpers';
import { STATE_ADD_TO_BAG } from '../../interactions/add-to-bag';
import { STATE_ADD_TO_BAG_MOBILE } from '../../interactions/add-to-bag-mobile';

const {
  productImages: {
    imageSets: { PLP },
  },
  responsive: { bp_1025: bp1025 },
  azLettersRegex,
} = getFrontConfig();

const SIZES = {
  IN_STORE_STOCK: '24vw',
};

export default {
  name: 'TheProductDetailsCustomizationModal',

  components: {
    BaseFieldText,
    BaseImage,
    BaseModal,
    BaseColorSwatches,
    BaseSpinner,
  },

  mixins: [
    bemClassesMixin,
    productBaseMixin,
    productImageSetMixin,
    productPropSizesMixin,
    windowResizeMixin,
  ],

  props: {
    addToCartUrl: {
      type: String,
      required: true,
    },
    addToCartUrlMobile: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp1025,
    selectedSizes: [],
    imageAttributeSizes: SIZES,
    pid: '',
    azLettersRegex,
    customizedText: '',
    customizedColor: '',
    STATE_ADD_TO_BAG,
    STATE_ADD_TO_BAG_MOBILE,
    isValid: false,
  }),

  computed: {
    mainImage() {
      return this.getProductImageSets(PLP)?.[2];
    },
  },

  mounted() {
    this.pid = getVariationGroupId(this.product.id);
    if (this.hasUniqueSize()) {
      this.selectedSizes.push(this.getUniqueSize());
    }
    this.$eventBus.$on(
      EventBusConfig.events.BaseColorSwatches.colorSelected,
      this.handleColorSelection
    );
  },

  beforeDestroy() {
    this.$eventBus.$off(
      EventBusConfig.events.BaseColorSwatches.colorSelected,
      this.handleColorSelection
    );
  },

  methods: {
    onBaseFieldTextChange() {
      const { customizeFieldText } = this.$refs;
      const { input } = customizeFieldText.$refs;
      customizeFieldText.clearError();
      clientSideValidation.functions.validateFields([input]);
      this.isValid = !customizeFieldText?.isInvalid;
    },
    handleColorSelection(color) {
      this.customizedColor = color;
    },
  },
};
</script>
